/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  FileSpreadsheet, 
  Download, 
  X, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  Filter, 
  Building2, 
  Search, 
  Check, 
  Sparkles, 
  Layers, 
  FileCheck2,
  Award,
  Paperclip,
  ShieldCheck
} from 'lucide-react';
import { CertificateRecord, CostCenter } from '../types.ts';
import { exportCertificatesToExcel, getCertificateValidityInfo } from '../utils/certificationsExcelExporter.ts';

interface CertExcelExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  certificates: CertificateRecord[];
  costCenters: CostCenter[];
  activeCostCenter?: CostCenter | null;
  initialFilterMode?: 'all' | 'expiring_30' | 'expired' | 'expiring_and_expired' | 'valid';
}

export const CertExcelExportModal: React.FC<CertExcelExportModalProps> = ({
  isOpen,
  onClose,
  certificates,
  costCenters,
  activeCostCenter,
  initialFilterMode = 'expiring_30'
}) => {
  // Filter States
  const [filterPreset, setFilterPreset] = useState<'all' | 'expiring_30' | 'expired' | 'expiring_and_expired' | 'valid'>(initialFilterMode);
  const [customDaysThreshold, setCustomDaysThreshold] = useState<number>(30);
  const [selectedCostCenterId, setSelectedCostCenterId] = useState<string>(activeCostCenter?.id || 'all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Options
  const [includeExpiringSheet, setIncludeExpiringSheet] = useState<boolean>(true);
  const [includeFilesSheet, setIncludeFilesSheet] = useState<boolean>(true);
  const [customReportTitle, setCustomReportTitle] = useState<string>('Matriz de Certificaciones, Auditorías y Sistemas de Gestión HSEQ');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);

  // Sync initial preset
  useEffect(() => {
    if (isOpen) {
      setFilterPreset(initialFilterMode);
      setExportSuccess(false);
    }
  }, [isOpen, initialFilterMode]);

  // Enrich certificates with real-time expiration
  const enrichedCerts = useMemo(() => {
    return certificates.map(c => ({
      ...c,
      validity: getCertificateValidityInfo(c.validUntil, c.status, customDaysThreshold)
    }));
  }, [certificates, customDaysThreshold]);

  // Filter logic
  const filteredCerts = useMemo(() => {
    return enrichedCerts.filter(cert => {
      // 1. Cost center
      if (selectedCostCenterId !== 'all') {
        if (cert.costCenterId && cert.costCenterId !== selectedCostCenterId) {
          return false;
        }
      }

      // 2. Category
      if (selectedCategory !== 'all' && cert.category !== selectedCategory) {
        return false;
      }

      // 3. Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const match = 
          (cert.name && cert.name.toLowerCase().includes(q)) ||
          (cert.code && cert.code.toLowerCase().includes(q)) ||
          (cert.issuer && cert.issuer.toLowerCase().includes(q)) ||
          (cert.scope && cert.scope.toLowerCase().includes(q)) ||
          (cert.leadAuditor && cert.leadAuditor.toLowerCase().includes(q));
        if (!match) return false;
      }

      // 4. Expiry preset
      const days = cert.validity.daysRemaining;
      if (filterPreset === 'expiring_30') {
        return days >= 0 && days <= customDaysThreshold;
      }
      if (filterPreset === 'expired') {
        return days < 0;
      }
      if (filterPreset === 'expiring_and_expired') {
        return days <= customDaysThreshold;
      }
      if (filterPreset === 'valid') {
        return days > customDaysThreshold && cert.validity.computedStatus !== 'en_auditoria';
      }

      return true;
    });
  }, [enrichedCerts, filterPreset, customDaysThreshold, selectedCostCenterId, selectedCategory, searchQuery]);

  // Statistics
  const stats = useMemo(() => {
    const total = filteredCerts.length;
    const expiringSoon = filteredCerts.filter(c => c.validity.daysRemaining >= 0 && c.validity.daysRemaining <= customDaysThreshold).length;
    const expired = filteredCerts.filter(c => c.validity.daysRemaining < 0).length;
    const valid = filteredCerts.filter(c => c.validity.daysRemaining > customDaysThreshold && c.validity.computedStatus !== 'en_auditoria').length;
    const files = filteredCerts.reduce((acc, c) => acc + (c.attachments?.length || 0), 0);
    return { total, expiringSoon, expired, valid, files };
  }, [filteredCerts, customDaysThreshold]);

  // Trigger export
  const handleExport = async () => {
    if (filteredCerts.length === 0) {
      alert('No hay certificaciones ni auditorías que coincidan con los filtros seleccionados.');
      return;
    }

    setIsExporting(true);
    setExportSuccess(false);

    try {
      let filterDesc = 'Todas las certificaciones y auditorías evaluadas';
      if (filterPreset === 'expiring_30') {
        filterDesc = `Filtrado específico: Certificaciones y auditorías próximas a vencer en ≤ ${customDaysThreshold} días para recertificación oportuna`;
      } else if (filterPreset === 'expired') {
        filterDesc = 'Filtrado específico: Auditorías y certificaciones vencidas';
      } else if (filterPreset === 'expiring_and_expired') {
        filterDesc = `Filtrado integral: Vencidas y próximas a vencer (≤ ${customDaysThreshold} días)`;
      } else if (filterPreset === 'valid') {
        filterDesc = `Filtrado: Certificaciones vigentes con más de ${customDaysThreshold} días de vigencia`;
      }

      if (selectedCostCenterId !== 'all') {
        const cc = costCenters.find(c => c.id === selectedCostCenterId);
        filterDesc += ` | Sede: ${cc ? cc.name : selectedCostCenterId}`;
      }
      if (selectedCategory !== 'all') {
        filterDesc += ` | Categoría: ${selectedCategory.toUpperCase()}`;
      }

      const activeCC = costCenters.find(c => c.id === selectedCostCenterId);
      const ccName = activeCC ? activeCC.name : (selectedCostCenterId === 'all' ? 'Todas las Sedes / Plantas' : selectedCostCenterId);

      const timestamp = new Date().toISOString().split('T')[0];
      let filePrefix = 'Reporte_Certificaciones_Auditorias_HSEQ';
      if (filterPreset === 'expiring_30') {
        filePrefix = `Certificaciones_Proximas_Vencer_${customDaysThreshold}Dias`;
      } else if (filterPreset === 'expired') {
        filePrefix = 'Auditorias_Certificaciones_Vencidas';
      }

      await exportCertificatesToExcel(filteredCerts, {
        fileName: `${filePrefix}_${timestamp}.xlsx`,
        reportTitle: customReportTitle,
        filterDescription: filterDesc,
        costCenterName: ccName,
        generatedBy: 'Coordinación de Calidad, Auditorías & HSEQ',
        includeExpiringOnlySheet: includeExpiringSheet,
        includeAttachmentsHistorySheet: includeFilesSheet,
        highlightExpiringDays: customDaysThreshold
      });

      setExportSuccess(true);
      setTimeout(() => {
        setExportSuccess(false);
      }, 4000);
    } catch (err) {
      console.error('[Certificates Excel Export] Error exporting:', err);
      alert('Ocurrió un error al generar el archivo Excel.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-[#14151a] border border-[#2d2f3d] rounded-2xl max-w-4xl w-full text-white shadow-2xl overflow-hidden flex flex-col max-h-[92vh] my-auto">
        
        {/* HEADER */}
        <div className="p-4 sm:p-5 bg-[#1a1c24] border-b border-[#2d2f3d] flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-950/80 border border-amber-500/50 flex items-center justify-center text-amber-400 shadow-md">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-bold text-white">
                  Exportar Certificaciones y Auditorías a Excel
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 font-bold uppercase">
                  Logo & Formato Pro
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Genere matrices con logotipo corporativo, filtros de vencimiento a 30 días, expediente de archivos y firmas de acreditación.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#252836] transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-5 text-xs flex-1">
          
          {/* 1. SELECCIÓN DE PRESET DE VENCIMIENTO */}
          <div className="space-y-2.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-amber-400" />
              <span>1. Filtro Principal por Estatus de Vencimiento:</span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              
              {/* Opción 1: Próximos a Vencer (30 Días) */}
              <button
                type="button"
                onClick={() => setFilterPreset('expiring_30')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer relative overflow-hidden ${
                  filterPreset === 'expiring_30'
                    ? 'bg-gradient-to-br from-amber-950/80 via-amber-900/40 to-[#1e1e24] border-amber-500 ring-1 ring-amber-500/50 shadow-lg'
                    : 'bg-[#181920] border-[#2c2e3d] hover:border-amber-500/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 font-bold text-amber-300 text-xs">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span>Próximas a Vencer (≤ {customDaysThreshold}d)</span>
                  </span>
                  {filterPreset === 'expiring_30' && (
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                  )}
                </div>
                <p className="text-[11px] text-slate-300 leading-snug">
                  Auditorías y acreditaciones en ventana de 30 días para renovación oportuna.
                </p>
                <div className="mt-2 text-[10px] font-mono font-bold text-amber-400 bg-amber-950/60 px-2 py-0.5 rounded inline-block border border-amber-500/40">
                  {enrichedCerts.filter(c => c.validity.daysRemaining >= 0 && c.validity.daysRemaining <= customDaysThreshold).length} Certificaciones detectadas
                </div>
              </button>

              {/* Opción 2: Vencidas */}
              <button
                type="button"
                onClick={() => setFilterPreset('expired')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  filterPreset === 'expired'
                    ? 'bg-gradient-to-br from-red-950/80 via-red-900/40 to-[#1e1e24] border-red-500 ring-1 ring-red-500/50 shadow-lg'
                    : 'bg-[#181920] border-[#2c2e3d] hover:border-red-500/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 font-bold text-red-300 text-xs">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span>Solo Auditorías Vencidas</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 leading-snug">
                  Acreditaciones con fecha límite expirada para trámite extraordinario.
                </p>
                <div className="mt-2 text-[10px] font-mono font-bold text-red-400 bg-red-950/60 px-2 py-0.5 rounded inline-block border border-red-500/40">
                  {enrichedCerts.filter(c => c.validity.daysRemaining < 0).length} Vencidas
                </div>
              </button>

              {/* Opción 3: Plan Total de Renovación */}
              <button
                type="button"
                onClick={() => setFilterPreset('expiring_and_expired')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  filterPreset === 'expiring_and_expired'
                    ? 'bg-gradient-to-br from-orange-950/80 via-orange-900/40 to-[#1e1e24] border-orange-500 ring-1 ring-orange-500/50 shadow-lg'
                    : 'bg-[#181920] border-[#2c2e3d] hover:border-orange-500/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 font-bold text-orange-300 text-xs">
                    <Sparkles className="w-4 h-4 text-orange-400" />
                    <span>Plan Recertificación Total</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 leading-snug">
                  Consolida vencidas y próximas a vencer en ≤ {customDaysThreshold} días.
                </p>
                <div className="mt-2 text-[10px] font-mono font-bold text-orange-400 bg-orange-950/60 px-2 py-0.5 rounded inline-block border border-orange-500/40">
                  {enrichedCerts.filter(c => c.validity.daysRemaining <= customDaysThreshold).length} En alerta
                </div>
              </button>
            </div>

            {/* Quick selectors */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => setFilterPreset('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
                  filterPreset === 'all'
                    ? 'bg-slate-700 text-white border border-slate-500'
                    : 'bg-[#181920] text-slate-400 hover:text-white border border-[#2c2e3d]'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Todas ({enrichedCerts.length})</span>
              </button>

              <button
                type="button"
                onClick={() => setFilterPreset('valid')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
                  filterPreset === 'valid'
                    ? 'bg-emerald-950 text-emerald-300 border border-emerald-500'
                    : 'bg-[#181920] text-slate-400 hover:text-emerald-400 border border-[#2c2e3d]'
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Solo Vigentes (&gt; {customDaysThreshold}d)</span>
              </button>

              {/* Ventana de días */}
              <div className="ml-auto flex items-center gap-2 bg-[#181920] px-2.5 py-1 rounded-lg border border-[#2c2e3d]">
                <span className="text-[11px] text-slate-400">Ventana de Alerta:</span>
                {[15, 30, 60, 90].map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setCustomDaysThreshold(days)}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono transition-colors cursor-pointer ${
                      customDaysThreshold === days
                        ? 'bg-amber-500 text-black font-bold'
                        : 'text-slate-400 hover:text-white bg-[#101115]'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 2. FILTROS ADICIONALES (SEDE, CATEGORIA, BÚSQUEDA) */}
          <div className="p-3.5 bg-[#181920] rounded-xl border border-[#282a36] space-y-3">
            <div className="flex items-center justify-between text-slate-300 font-bold text-[11px] uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5 text-sky-400" />
                <span>2. Filtros Específicos por Sede y Norma:</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedCostCenterId('all');
                  setSelectedCategory('all');
                  setSearchQuery('');
                }}
                className="text-[10px] text-slate-400 hover:text-white underline cursor-pointer"
              >
                Restablecer Filtros
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Sede */}
              <div>
                <label className="text-[10px] text-slate-400 block mb-1 font-semibold flex items-center gap-1">
                  <Building2 className="w-3 h-3 text-emerald-400" />
                  <span>Sede Operativa / Planta:</span>
                </label>
                <select
                  value={selectedCostCenterId}
                  onChange={(e) => setSelectedCostCenterId(e.target.value)}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="all">Todas las Sedes / Plantas</option>
                  {costCenters.map(cc => (
                    <option key={cc.id} value={cc.id}>{cc.name} ({cc.code || cc.id})</option>
                  ))}
                </select>
              </div>

              {/* Categoría */}
              <div>
                <label className="text-[10px] text-slate-400 block mb-1 font-semibold flex items-center gap-1">
                  <Award className="w-3 h-3 text-amber-400" />
                  <span>Categoría de Certificación:</span>
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="all">Todas las Categorías</option>
                  <option value="calidad">Calidad (ISO 9001 / IATF 16949)</option>
                  <option value="ambiental">Medio Ambiente (ISO 14001)</option>
                  <option value="seguridad">Seguridad y Salud (ISO 45001 / STPS)</option>
                  <option value="proteccion_civil">Protección Civil / Dictamen Bomberos</option>
                  <option value="metrologia">Metrología y Calibración</option>
                  <option value="auditoria_cliente">Auditorías de Cliente (VDA / OEM)</option>
                </select>
              </div>

              {/* Búsqueda */}
              <div>
                <label className="text-[10px] text-slate-400 block mb-1 font-semibold flex items-center gap-1">
                  <Search className="w-3 h-3 text-sky-400" />
                  <span>Buscar por Norma, Ente o Auditor:</span>
                </label>
                <input
                  type="text"
                  placeholder="Ej: ISO 9001, TÜV, Auditor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
                />
              </div>
            </div>
          </div>

          {/* 3. VISTA PREVIA Y CONTADOR */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                <FileCheck2 className="w-3.5 h-3.5 text-amber-400" />
                <span>3. Resumen de Registros a Exportar en Excel:</span>
              </span>
              <span className="text-[11px] font-mono text-amber-400 font-bold bg-amber-950/80 px-2 py-0.5 rounded border border-amber-600/40">
                {stats.total} Certificaciones Seleccionadas ({stats.files} Archivos)
              </span>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-[#101115] p-2.5 rounded-xl border border-[#262836]">
              <div className="text-center p-1.5 bg-[#181920] rounded-lg">
                <span className="text-[10px] text-slate-400 block font-semibold">Total a Exportar</span>
                <strong className="text-sm text-white font-mono">{stats.total}</strong>
              </div>
              <div className="text-center p-1.5 bg-[#181920] rounded-lg border border-amber-500/20">
                <span className="text-[10px] text-amber-300 block font-semibold">Vencen en ≤ {customDaysThreshold}d</span>
                <strong className="text-sm text-amber-400 font-mono">{stats.expiringSoon}</strong>
              </div>
              <div className="text-center p-1.5 bg-[#181920] rounded-lg border border-red-500/20">
                <span className="text-[10px] text-red-300 block font-semibold">Vencidas</span>
                <strong className="text-sm text-red-400 font-mono">{stats.expired}</strong>
              </div>
              <div className="text-center p-1.5 bg-[#181920] rounded-lg border border-emerald-500/20">
                <span className="text-[10px] text-emerald-300 block font-semibold">Archivos Adjuntos</span>
                <strong className="text-sm text-emerald-400 font-mono">{stats.files}</strong>
              </div>
            </div>

            {/* Preview table */}
            <div className="max-h-48 overflow-y-auto overflow-x-auto rounded-xl border border-[#262836] bg-[#101115]">
              <table className="w-full text-left text-[11px]">
                <thead className="bg-[#181922] text-slate-400 font-mono uppercase text-[9px] sticky top-0 border-b border-[#2c2e3d]">
                  <tr>
                    <th className="p-2">Norma / Código</th>
                    <th className="p-2">Certificación</th>
                    <th className="p-2">Ente Emisor</th>
                    <th className="p-2">Vencimiento</th>
                    <th className="p-2">Días Rest.</th>
                    <th className="p-2">Archivos</th>
                    <th className="p-2">Estatus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e2029]">
                  {filteredCerts.slice(0, 40).map((cert) => (
                    <tr key={cert.id} className="hover:bg-[#161720]">
                      <td className="p-2 font-mono font-bold text-amber-400 whitespace-nowrap">
                        {cert.code}
                      </td>
                      <td className="p-2 text-white font-semibold">
                        {cert.name}
                        <span className="text-[9px] text-slate-400 block font-normal">{cert.scope}</span>
                      </td>
                      <td className="p-2 text-slate-300 whitespace-nowrap">
                        {cert.issuer}
                      </td>
                      <td className="p-2 font-mono font-bold text-white whitespace-nowrap">
                        {cert.validity.expiryDate}
                      </td>
                      <td className="p-2 font-mono whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded font-bold ${
                          cert.validity.daysRemaining < 0
                            ? 'bg-red-950 text-red-300'
                            : cert.validity.daysRemaining <= customDaysThreshold
                            ? 'bg-amber-950 text-amber-300'
                            : 'text-emerald-400'
                        }`}>
                          {cert.validity.daysRemaining}d
                        </span>
                      </td>
                      <td className="p-2 whitespace-nowrap text-slate-300">
                        <span className="flex items-center gap-1 font-mono text-[10px]">
                          <Paperclip className="w-3 h-3 text-slate-400" />
                          {cert.attachments?.length || 0}
                        </span>
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {cert.validity.computedStatus === 'vencido' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-950 text-red-300 border border-red-700">
                            Vencido
                          </span>
                        )}
                        {cert.validity.computedStatus === 'proximo_vencer' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-950 text-amber-300 border border-amber-700">
                            Por Vencer (≤{customDaysThreshold}d)
                          </span>
                        )}
                        {cert.validity.computedStatus === 'vigente' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-700">
                            Vigente
                          </span>
                        )}
                        {cert.validity.computedStatus === 'en_auditoria' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-950 text-sky-300 border border-sky-700">
                            En Auditoría
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredCerts.length === 0 && (
                <div className="p-6 text-center text-slate-400">
                  No hay certificaciones ni auditorías que cumplan con los filtros seleccionados.
                </div>
              )}
            </div>
          </div>

          {/* 4. OPCIONES DE FORMATO EXCEL & MULTI-HOJA */}
          <div className="p-3.5 bg-[#181920] rounded-xl border border-[#282a36] space-y-2.5">
            <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
              4. Opciones de Formato y Hojas del Libro Excel:
            </span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2 p-2 rounded-lg bg-[#101115] border border-[#232530] cursor-pointer hover:border-slate-600">
                <input
                  type="checkbox"
                  checked={includeExpiringSheet}
                  onChange={(e) => setIncludeExpiringSheet(e.target.checked)}
                  className="rounded text-amber-500 focus:ring-amber-500"
                />
                <span className="text-slate-200">
                  Incluir Hoja <strong className="text-amber-300">"Alertas de Renovación ({customDaysThreshold} Días)"</strong>
                </span>
              </label>

              <label className="flex items-center gap-2 p-2 rounded-lg bg-[#101115] border border-[#232530] cursor-pointer hover:border-slate-600">
                <input
                  type="checkbox"
                  checked={includeFilesSheet}
                  onChange={(e) => setIncludeFilesSheet(e.target.checked)}
                  className="rounded text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-slate-200">
                  Incluir Hoja <strong className="text-indigo-300">"Histórico de Archivos y Evidencias"</strong>
                </span>
              </label>
            </div>

            <div className="text-[11px] text-slate-400 bg-amber-950/20 border border-amber-500/30 p-2 rounded-lg flex items-center gap-2">
              <Check className="w-4 h-4 text-amber-400 shrink-0" />
              <span>
                El libro Excel incluirá el <strong>Logotipo Institucional en alta resolución</strong>, paleta de colores azul marino y dorado, márgenes de impresión, tarjetas de KPI y bloque de firmas de <strong>Acreditación HSEQ</strong>.
              </span>
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div className="p-4 bg-[#1a1c24] border-t border-[#2d2f3d] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {exportSuccess && (
              <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5 animate-pulse">
                <CheckCircle2 className="w-4 h-4" />
                ¡Libro Excel de Certificaciones descargado exitosamente!
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-[#252836] hover:bg-slate-700 text-slate-300 text-xs font-semibold cursor-pointer transition-colors"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting || filteredCerts.length === 0}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 hover:from-amber-500 hover:to-orange-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-amber-950/50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Generando Libro Excel...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 text-amber-200" />
                  <span>Descargar Matriz Excel ({stats.total})</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
